import { useEffect, useRef } from 'react'
import mapboxgl from 'mapbox-gl'
import { Bike } from 'lucide-react'
import { renderToString } from 'react-dom/server'
import DOMPurify from 'dompurify'
import { abbreviateLocation } from '../../utils/mapHelpers'
import { sanitizeUrl } from '../../utils/urlSanitizer'

interface ClubMarker {
  id: string
  name: string
  description: string
  location: string
  coordinates: { lat: number; lng: number }
  memberCount: number
  routeCount: number
  logoURL?: string
  website?: string
  membershipType?: 'open' | 'approval' | 'invite'
  isPublic?: boolean
  clubTypes?: string[]
}

interface ClubMarkersProps {
  map: mapboxgl.Map | null
  clubMarkers: ClubMarker[]
  selectedClubId?: string | null
  onClubMarkerClick?: (club: ClubMarker) => void
}

export function ClubMarkers({ map, clubMarkers, selectedClubId, onClubMarkerClick }: ClubMarkersProps) {
  const markersRef = useRef<mapboxgl.Marker[]>([])

  useEffect(() => {
    if (!map || !clubMarkers || clubMarkers.length === 0) return

    // Clear existing markers first
    markersRef.current.forEach(marker => marker.remove())
    markersRef.current = []

    // Group clubs by coordinates and add small offsets for overlapping locations
    const adjustedClubs = clubMarkers.map((club, index) => {
      if (!club.coordinates) return club

      // Find other clubs with same coordinates
      const duplicates = clubMarkers.filter((otherClub, otherIndex) => 
        otherIndex !== index &&
        otherClub.coordinates &&
        Math.abs(otherClub.coordinates.lat - club.coordinates.lat) < 0.0001 &&
        Math.abs(otherClub.coordinates.lng - club.coordinates.lng) < 0.0001
      )

      if (duplicates.length === 0) return club

      // Add small random offset (about 100m radius)
      const angle = (index * 137.5) % 360 // Golden angle for even distribution
      const radius = 0.001 + (index % duplicates.length) * 0.0005 // Small offset
      const offsetLat = radius * Math.cos(angle * Math.PI / 180)
      const offsetLng = radius * Math.sin(angle * Math.PI / 180)

      return {
        ...club,
        coordinates: {
          lat: club.coordinates.lat + offsetLat,
          lng: club.coordinates.lng + offsetLng
        }
      }
    })

    // Add club markers with adjusted coordinates
    adjustedClubs.forEach((club) => {
      if (!club.coordinates) return

      // Create club marker element (same style as homepage route markers)
      const markerElement = document.createElement('div')
      markerElement.className = 'club-marker'
      markerElement.setAttribute('data-club-id', club.id)
      markerElement.innerHTML = `
        <div class="marker-container" style="
          position: relative;
          width: 24px;
          height: 30px;
          cursor: pointer;
          transition: transform 0.2s ease;
        ">
          <div class="marker-pin" style="
            width: 24px;
            height: 24px;
            background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
            border: 2px solid white;
            border-radius: 50% 50% 50% 0;
            transform: rotate(-45deg);
            box-shadow: 0 3px 10px rgba(0,0,0,0.3);
            position: absolute;
            top: 0;
            left: 0;
            transition: all 0.2s ease;
          "></div>
          <div class="marker-icon" style="
            position: absolute;
            top: 7px;
            left: 7px;
            z-index: 2;
            transition: all 0.2s ease;
          ">
            ${renderToString(<Bike size={10} stroke="white" strokeWidth={2.5} />)}
          </div>
        </div>
      `

      // Set initial style based on selection state
      const updateMarkerStyle = (isHovered: boolean = false) => {
        const isSelected = selectedClubId === club.id
        const pin = markerElement.querySelector('.marker-pin') as HTMLElement
        const container = markerElement.querySelector('.marker-container') as HTMLElement
        
        if (pin && container) {
          if (isSelected || isHovered) {
            pin.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
            pin.style.transform = 'rotate(-45deg) scale(1.1)'
            container.style.transform = 'scale(1.1)'
          } else {
            pin.style.background = 'linear-gradient(135deg, #1f2937 0%, #374151 100%)'
            pin.style.transform = 'rotate(-45deg) scale(1)'
            container.style.transform = 'scale(1)'
          }
        }
      }

      // Set initial style
      updateMarkerStyle()

      // Add hover effects
      markerElement.addEventListener('mouseenter', () => {
        updateMarkerStyle(true)
      })

      markerElement.addEventListener('mouseleave', () => {
        updateMarkerStyle(false)
      })

      // Create popup content (styled like ClubCard)
      const popupContent = createClubPopupContent(club)

      // Create popup
      const popup = new mapboxgl.Popup({
        offset: [0, -40],
        closeButton: false,
        closeOnClick: false,
        className: 'club-popup'
      }).setHTML(popupContent)

      // Add click handler to marker element if callback provided
      if (onClubMarkerClick) {
        markerElement.addEventListener('click', (e) => {
          e.stopPropagation()
          onClubMarkerClick(club)
        })
        
        // Make cursor pointer to indicate clickability
        markerElement.style.cursor = 'pointer'
      }

      // Create marker
      const marker = new mapboxgl.Marker({
        element: markerElement
      })
        .setLngLat([club.coordinates.lng, club.coordinates.lat])
        .addTo(map)

      // Only add popup if no click handler provided (fallback)
      if (!onClubMarkerClick) {
        marker.setPopup(popup)
      }

      markersRef.current.push(marker)
    })

    return () => {
      markersRef.current.forEach(marker => marker.remove())
      markersRef.current = []
    }
  }, [map, clubMarkers, selectedClubId, onClubMarkerClick])

  // Update marker styles when selectedClubId changes
  useEffect(() => {
    if (!map) return

    document.querySelectorAll('.club-marker').forEach(markerElement => {
      const clubId = markerElement.getAttribute('data-club-id')
      const isSelected = selectedClubId === clubId
      
      const pin = markerElement.querySelector('.marker-pin') as HTMLElement
      const container = markerElement.querySelector('.marker-container') as HTMLElement
      
      if (pin && container) {
        if (isSelected) {
          pin.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)'
          pin.style.transform = 'rotate(-45deg) scale(1.1)'
          container.style.transform = 'scale(1.1)'
        } else {
          pin.style.background = 'linear-gradient(135deg, #1f2937 0%, #374151 100%)'
          pin.style.transform = 'rotate(-45deg) scale(1)'
          container.style.transform = 'scale(1)'
        }
      }
    })
  }, [selectedClubId, map])

  return null
}

function createClubPopupContent(club: ClubMarker): string {
  // Sanitize all user inputs to prevent XSS
  const safeName = DOMPurify.sanitize(club.name, { ALLOWED_TAGS: [] })
  const safeLocation = DOMPurify.sanitize(abbreviateLocation(club.location), { ALLOWED_TAGS: [] })
  const safeLogoURL = club.logoURL ? sanitizeUrl(club.logoURL) : ''
  const safeWebsite = club.website ? sanitizeUrl(club.website) : ''
  const safeClubTypes = club.clubTypes ? club.clubTypes.map(type => DOMPurify.sanitize(type, { ALLOWED_TAGS: [] })) : []
  
  return `
    <div style="
      width: 280px;
      background: #374151;
      border: 1px solid #4b5563;
      border-radius: 8px;
      overflow: hidden;
      font-family: system-ui, -apple-system, sans-serif;
    ">
      <!-- Club Header -->
      <div style="padding: 16px; position: relative;">
        <!-- Close Button -->
        <button onclick="this.closest('.mapboxgl-popup').remove()" style="
          position: absolute;
          top: 8px;
          right: 8px;
          background: rgba(0,0,0,0.5);
          border: none;
          border-radius: 50%;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          z-index: 10;
        ">
          <svg width="12" height="12" fill="none" stroke="white" stroke-width="2" viewBox="0 0 24 24">
            <path d="M18 6L6 18M6 6l12 12"/>
          </svg>
        </button>
        <div style="display: flex; align-items: flex-start; gap: 12px; margin-bottom: 12px;">
          <!-- Club Logo -->
          <div style="flex-shrink: 0;">
            ${safeLogoURL ? 
              `<img src="${safeLogoURL}" alt="${safeName} logo" style="width: 48px; height: 48px; border-radius: 8px; object-fit: cover;">` :
              `<div style="width: 48px; height: 48px; background: linear-gradient(135deg, #10b981, #059669); border-radius: 8px; display: flex; align-items: center; justify-content: center;">
                 <svg width="24" height="24" fill="none" stroke="white" stroke-width="2" viewBox="0 0 24 24">
                   <path d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/>
                 </svg>
               </div>`
            }
          </div>
          
          <!-- Club Info -->
          <div style="flex: 1; min-width: 0;">
            <h3 style="margin: 0 0 4px 0; font-size: 18px; font-weight: 600; color: white; line-height: 1.3;">${safeName}</h3>
            <!-- Location -->
            <div style="display: flex; align-items: center; color: #9ca3af; font-size: 14px; margin-bottom: 12px;">
              <svg width="12" height="12" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" style="margin-right: 4px; flex-shrink: 0;">
                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z"/>
                <circle cx="12" cy="10" r="3"/>
              </svg>
              <span style="white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">${safeLocation}</span>
            </div>
          </div>
        </div>
        
        <!-- Stats -->
        <div style="display: flex; align-items: center; justify-content: space-between; margin-bottom: 12px;">
          <div style="display: flex; gap: 16px;">
            <!-- Member Count -->
            <div style="display: flex; align-items: center; color: #9ca3af;">
              <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" style="margin-right: 4px;">
                <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/>
                <circle cx="9" cy="7" r="4"/>
                <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
              </svg>
              <span style="font-size: 14px;">${club.memberCount}</span>
            </div>
            
            <!-- Route Count -->
            <div style="display: flex; align-items: center; color: #9ca3af;">
              <svg width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" style="margin-right: 4px;">
                <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
              </svg>
              <span style="font-size: 14px;">${club.routeCount}</span>
            </div>
          </div>
        </div>
        
        <!-- Club Types -->
        ${safeClubTypes.length > 0 ? `
          <div style="margin-bottom: 12px;">
            <div style="display: flex; flex-wrap: wrap; gap: 4px;">
              ${safeClubTypes.map(type => `
                <span style="
                  background: #06b6d4; 
                  color: white; 
                  padding: 2px 8px; 
                  border-radius: 4px; 
                  font-size: 12px;
                  white-space: nowrap;
                ">${type}</span>
              `).join('')}
            </div>
          </div>
        ` : ''}
        
        <!-- Membership Type -->
        <div style="margin-bottom: 12px;">
          <span style="
            background: ${(club.membershipType || 'open') === 'open' 
              ? '#10b981' 
              : (club.membershipType || 'open') === 'approval'
              ? '#f59e0b'
              : '#ef4444'}; 
            color: white; 
            padding: 4px 8px; 
            border-radius: 4px; 
            font-size: 12px;
          ">
            ${(club.membershipType || 'open') === 'open' ? 'Open Membership' : 
              (club.membershipType || 'open') === 'approval' ? 'Approval Required' : 'Invite Only'}
          </span>
        </div>
        
        <!-- Actions -->
        <div style="display: flex; gap: 8px;">
          <a href="/club/${club.id}" style="
            flex: 1;
            background: #10b981;
            color: white;
            text-decoration: none;
            padding: 8px 12px;
            border-radius: 6px;
            font-size: 14px;
            font-weight: 500;
            text-align: center;
            display: block;
            transition: background-color 0.2s;
          " onmouseover="this.style.background='#059669'" onmouseout="this.style.background='#10b981'">View Club</a>
          ${safeWebsite && safeWebsite !== '#' ? 
            `<a href="${safeWebsite}" target="_blank" style="
              background: #4b5563;
              color: #d1d5db;
              text-decoration: none;
              padding: 8px 12px;
              border-radius: 6px;
              font-size: 14px;
              font-weight: 500;
              text-align: center;
              display: block;
              transition: background-color 0.2s;
            " onmouseover="this.style.background='#374151'" onmouseout="this.style.background='#4b5563'">Website</a>` : ''
          }
        </div>
      </div>
      
      <!-- Hover Effect Bar -->
      <div style="height: 4px; background: linear-gradient(90deg, #10b981, #06b6d4); border-radius: 0 0 8px 8px;"></div>
    </div>
  `
}